// Types for request/response objects
/** Represents a secret configuration for API access */
export type SecretProperties = {
  api_domain?: string;
  api_path_prefix?: string;
  auth_header_key?: string;
  auth_header_value?: string;
};

/** Represents a mapping between a miniapp tier and a secret */
export type TierMapping = {
  miniapp_name: string;
  creator_id: string;
  secret_reference: string;
  tier: string;
};

/** Represents a tier assignment for a user */
export type TierAssignment = {
  miniapp_name: string;
  user_id: string;
  tier: string;
};

/** Represents rate limit configuration for a tier */
export type RateLimit = {
  miniapp_name: string;
  secret_reference: string;
  tier: string;
  window_ms: number;
  limit: number;
};

/** Configuration for the proxy fetch client */
export type ProxyFetchConfig = {
  miniappName: string;
};

/** Configuration for the proxy getView client */
export type ProxyGetViewConfig = {
  miniappName: string;
  gwProxyUrl: string;
};

const APIProxyHandler = (baseUrl: string, projectHmac?: string) => {
  const BASE_API_URL = baseUrl;

  // Helper to create headers with HMAC when needed
  const createHeaders = (includeHmac = false, customHmac?: string) => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (includeHmac) {
      const hmacToUse = customHmac || projectHmac;
      if (hmacToUse) {
        headers['x-sxt-project-hmac'] = hmacToUse;
      }
    }
    return headers;
  };

  // Secrets Management
  const getSecrets = async () => {
    return await fetch(`${BASE_API_URL}/config/secrets`, {
      method: 'GET',
      headers: createHeaders(false),
    });
  };

  const putSecret = async (secretReference: string, secret: SecretProperties) => {
    return await fetch(`${BASE_API_URL}/config/secrets/${secretReference}`, {
      method: 'PUT',
      headers: createHeaders(false),
      body: JSON.stringify(secret),
    });
  };

  const patchSecret = async (secretReference: string, secret: SecretProperties) => {
    return await fetch(`${BASE_API_URL}/config/secrets/${secretReference}`, {
      method: 'PATCH',
      headers: createHeaders(false),
      body: JSON.stringify(secret),
    });
  };

  const getSecret = async (secretReference: string) => {
    return await fetch(`${BASE_API_URL}/config/secrets/${secretReference}`, {
      method: 'GET',
      headers: createHeaders(false),
    });
  };

  const deleteSecret = async (secretReference: string) => {
    return await fetch(`${BASE_API_URL}/config/secrets/${secretReference}`, {
      method: 'DELETE',
      headers: createHeaders(false),
    });
  };

  // Miniapp Tier-Key Mappings
  const getAllTierKeyMaps = async () => {
    return await fetch(`${BASE_API_URL}/config/miniapps/*/tier-key-map`, {
      method: 'GET',
      headers: createHeaders(false), // No HMAC needed for wildcard
    });
  };

  const getTierKeyMap = async (miniappName: string, customHmac?: string) => {
    return await fetch(`${BASE_API_URL}/config/miniapps/${miniappName}/tier-key-map`, {
      method: 'GET',
      headers: createHeaders(true, customHmac),
    });
  };

  const putTierKeyMap = async (
    miniappName: string,
    tier: string,
    secretReference: string,
    customHmac?: string
  ) => {
    return await fetch(`${BASE_API_URL}/config/miniapps/${miniappName}/tier-key-map/${tier}`, {
      method: 'PUT',
      headers: createHeaders(true, customHmac),
      body: JSON.stringify({ secret_reference: secretReference }),
    });
  };

  const getTierKeyMapForTier = async (miniappName: string, tier: string, customHmac?: string) => {
    return await fetch(`${BASE_API_URL}/config/miniapps/${miniappName}/tier-key-map/${tier}`, {
      method: 'GET',
      headers: createHeaders(true, customHmac),
    });
  };

  const deleteTierKeyMap = async (
    miniappName: string,
    tier: string,
    secretReference: string,
    customHmac?: string
  ) => {
    return await fetch(
      `${BASE_API_URL}/config/miniapps/${miniappName}/tier-key-map/${tier}/${secretReference}`,
      {
        method: 'DELETE',
        headers: createHeaders(true, customHmac),
      }
    );
  };

  // Tier Assignments
  const getTierAssignments = async (miniappName: string, customHmac?: string) => {
    return await fetch(`${BASE_API_URL}/config/miniapps/${miniappName}/assignments`, {
      method: 'GET',
      headers: createHeaders(true, customHmac),
    });
  };

  const putTierAssignment = async (
    miniappName: string,
    userId: string,
    tier: string,
    customHmac?: string
  ) => {
    return await fetch(`${BASE_API_URL}/config/miniapps/${miniappName}/assignments/${userId}`, {
      method: 'PUT',
      headers: createHeaders(true, customHmac),
      body: JSON.stringify({ tier }),
    });
  };

  const getTierAssignment = async (miniappName: string, userId: string, customHmac?: string) => {
    return await fetch(`${BASE_API_URL}/config/miniapps/${miniappName}/assignments/${userId}`, {
      method: 'GET',
      headers: createHeaders(true, customHmac),
    });
  };

  const deleteTierAssignment = async (miniappName: string, userId: string, customHmac?: string) => {
    return await fetch(`${BASE_API_URL}/config/miniapps/${miniappName}/assignments/${userId}`, {
      method: 'DELETE',
      headers: createHeaders(true, customHmac),
    });
  };

  // Rate Limits
  const getRateLimits = async (miniappName: string, customHmac?: string) => {
    return await fetch(`${BASE_API_URL}/config/miniapps/${miniappName}/rate-limits`, {
      method: 'GET',
      headers: createHeaders(true, customHmac),
    });
  };

  const putRateLimit = async (
    miniappName: string,
    tier: string,
    secretReference: string,
    rateLimit: Pick<RateLimit, 'window_ms' | 'limit'>,
    customHmac?: string
  ) => {
    return await fetch(
      `${BASE_API_URL}/config/miniapps/${miniappName}/rate-limits/${tier}/${secretReference}`,
      {
        method: 'PUT',
        headers: createHeaders(true, customHmac),
        body: JSON.stringify(rateLimit),
      }
    );
  };

  const getRateLimit = async (
    miniappName: string,
    tier: string,
    secretReference: string,
    customHmac?: string
  ) => {
    return await fetch(
      `${BASE_API_URL}/config/miniapps/${miniappName}/rate-limits/${tier}/${secretReference}`,
      {
        method: 'GET',
        headers: createHeaders(true, customHmac),
      }
    );
  };

  const deleteRateLimit = async (
    miniappName: string,
    tier: string,
    secretReference: string,
    customHmac?: string
  ) => {
    return await fetch(
      `${BASE_API_URL}/config/miniapps/${miniappName}/rate-limits/${tier}/${secretReference}`,
      {
        method: 'DELETE',
        headers: createHeaders(true, customHmac),
      }
    );
  };

  /**
   * Creates a fetch-compatible function that proxies requests through the API Proxy service.
   * @param config Configuration for the proxy, including the miniapp name
   * @returns A function with the same interface as fetch that proxies requests
   * @example
   * const proxyFetch = createProxyFetch({ miniappName: 'my-miniapp' });
   * const response = await proxyFetch('https://api.example.com/data');
   */
  const createProxyFetch = (config: ProxyFetchConfig) => {
    return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const url = input instanceof URL ? input : new URL(input.toString());
      return await fetch(
        `${BASE_API_URL}/proxy/${config.miniappName}/${url.host}${url.pathname}${url.search}${url.hash}`,
        init
      );
    };
  };

  /**
   * Creates a function for getting SxT views from the GW proxy
   * @param config Configuration for the proxy, including the miniapp name and GW proxy base URL
   * @returns A function for querying SxT views through the Dreamspace proxy
   * @example
   * const proxyGetView = createProxyGetView({ miniappName: 'my-miniapp', gwBaseUrl: 'https://gw.example.com' });
   * const response = await proxyGetView('public', 'users_view', { id: 123 });
   */
  const createProxyGetView = (config: ProxyGetViewConfig) => {
    const host = new URL(config.gwProxyUrl).hostname;
    return async (
      schema: string,
      viewName: string,
      parameters: { [key: string]: string | number | boolean } = {}
    ): Promise<{ [key: string]: any }[]> => {
      let sqlText = `SELECT * FROM ${schema}.${viewName}`;
      if (parameters && Object.keys(parameters).length > 0) {
        sqlText += `(${Object.entries(parameters)
          .map(([key, value]) => `'${key}=${value}'`)
          .join(',')})`;
      }

      const response = await fetch(
        `${BASE_API_URL}/proxy/${config.miniappName}/${host}/v1/sql/no-injected-authz`,
        {
          method: 'POST',
          headers: createHeaders(false),
          body: JSON.stringify({
            sqlText: sqlText,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch view: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    };
  };

  return {
    // Secrets
    getSecrets,
    putSecret,
    patchSecret,
    getSecret,
    deleteSecret,

    // Tier-Key Mappings
    getAllTierKeyMaps,
    getTierKeyMap,
    putTierKeyMap,
    getTierKeyMapForTier,
    deleteTierKeyMap,

    // Tier Assignments
    getTierAssignments,
    putTierAssignment,
    getTierAssignment,
    deleteTierAssignment,

    // Rate Limits
    getRateLimits,
    putRateLimit,
    getRateLimit,
    deleteRateLimit,

    // Proxy
    createProxyFetch,
    createProxyGetView,
  };
};

export { APIProxyHandler };
